import React, { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import './scss/fees.scss';
import Header from './Header';
import api from './services/apiService';
import stripeService from './services/stripeService';

const initialState = {
  games: [],
  transactions: [],
  summary: {
    total_games: 0,
    total_cost: 0,
    total_payments: 0,
    current_balance: 0,
    team_breakdown: []
  },
  teams: []
};

const FeesComponent = () => {
  const [paymentData, setPaymentData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [playerId, setPlayerId] = useState(sessionStorage.getItem('player_id') || '');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        if (!playerId) {
          setIsLoading(false);
          return;
        }

        const url = `${process.env.REACT_APP_API_URL}/get_payment_data.php?player_id=${playerId}`;

        const response = await api.get(url);
        const result = response.data;
        
        if (result.success) {
          setPaymentData(result.data);
        } else {
          console.error('Failed to fetch payments');
          setPaymentData(initialState);
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
        setPaymentData(initialState);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [playerId]);

  const handlePayment = async (amount, matchId = 'balance') => {
    setIsProcessingPayment(true);
    setPaymentError(null);
    
    try {
      const teamId = sessionStorage.getItem('selected_team_id');

      console.log('Payment details:', { matchId, playerId, amount, teamId });

      if (!playerId || !teamId) {
        throw new Error('Missing required data: player ID or team ID');
      }

      // Use the smart createAndOpenCheckout method from stripeService
      await stripeService.createAndOpenCheckout({
        matchId: 'balance', // Always use 'balance' for payments
        playerId: parseInt(playerId, 10),
        amount: parseFloat(amount),
        teamId: parseInt(teamId, 10)
      });
      
    } catch (error) {
      console.error('Error in handlePayment:', error);
      setPaymentError(error.message || 'An error occurred during payment');
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const hasNegativeBalance = parseFloat(paymentData?.summary?.current_balance || 0) < 0;
  const isPlayerOne = playerId === '1';
  const showPaymentButton = hasNegativeBalance && isPlayerOne;
  const hasMultipleTeams = Array.isArray(paymentData?.teams) && paymentData.teams.length > 1;

  return (
    <div className="Fees container">
      <Header />
      <h1>Match Fees</h1>
      
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <div className="summary-section">
            <h2>Summary</h2>
            <p>
              <span>Total Games:</span>
              <span>{paymentData?.summary?.total_games || 0}</span>
            </p>
            <p>
              <span>Total Cost:</span>
              <span>£{paymentData?.summary?.total_cost || 0}</span>
            </p>
            <p>
              <span>Total Payments:</span>
              <span>£{paymentData?.summary?.total_payments || 0}</span>
            </p>
            <p>
              <span>Current Balance:</span>
              <span className={hasNegativeBalance ? 'balance-negative' : 'balance-positive'}>
                £{paymentData?.summary?.current_balance || 0}
              </span>
            </p>
            {showPaymentButton && (
              <>
                <button 
                  className="payment-button"
                  onClick={() => handlePayment(
                    Math.abs(parseFloat(paymentData?.summary?.current_balance || 0))
                  )}
                  disabled={isProcessingPayment}
                >
                  {isProcessingPayment ? 'Processing...' : 'Pay Outstanding Balance'}
                </button>
                
                {paymentError && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '10px', 
                    backgroundColor: '#FFD2D2',
                    color: '#D8000C',
                    borderRadius: '4px'
                  }}>
                    <p><strong>Payment Error:</strong> {paymentError}</p>
                    <p>Please try again or contact support if the issue persists.</p>
                  </div>
                )}
              </>
            )}
          </div>

          {Array.isArray(paymentData?.summary?.team_breakdown) && 
           paymentData.summary.team_breakdown.length > 0 && (
            <div className="team-breakdown">
              <h2>Team Breakdown</h2>
              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Games</th>
                    <th>Cost</th>
                    <th>Payments</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentData.summary.team_breakdown.map((team) => (
                    <tr key={team.team_id}>
                      <td>{team.team_name}</td>
                      <td>{team.games}</td>
                      <td>£{team.cost}</td>
                      <td>£{team.payments}</td>
                      <td className={parseFloat(team.balance) < 0 ? 'balance-negative' : 'balance-positive'}>
                        £{team.balance}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>Transactions</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Team</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(paymentData?.transactions) && paymentData.transactions.map((transaction) => (
                <tr key={transaction.transaction_ids}>
                  <td>{new Date(transaction.date).toLocaleDateString()}</td>
                  <td>{transaction.team_name}</td>
                  <td>£{transaction.amount}</td>
                  <td>{transaction.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Recent Games</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Team</th>
                <th>location</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(paymentData?.games) && paymentData.games.map((game) => (
                <tr key={game.match_id}>
                  <td>{new Date(game.match_date).toLocaleDateString()}</td>
                  <td>{game.team_name}</td>
                  <td>{game.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const Fees = memo(FeesComponent);

// Ensure component is properly named in dev tools
Fees.displayName = 'Fees';

export default Fees; 